import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import { motion } from 'framer-motion';
import {
    ArrowRight,
    Briefcase,
    ChevronRight,
    ClipboardList,
    Clock,
    Star,
    TrendingUp,
    Users
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import api, { invoiceAPI } from '../utils/api';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      when: "beforeChildren",
      staggerChildren: 0.2
    } 
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

const Dashboard = () => {
  const [timeframe, setTimeframe] = useState('month');
  
  // Loading indicator for dashboard fetch
  const [dashboardLoading, setDashboardLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState([
    { title: 'Active Jobs', value: '--', icon: Briefcase, color: 'bg-primary-100 text-primary-700' },
    { title: 'Total Applications', value: '--', icon: ClipboardList, color: 'bg-accent-100 text-accent-700' },
    { title: 'New Candidates', value: '--', icon: Users, color: 'bg-warning-100 text-warning-700' },
    { title: 'Time to Hire', value: '--', icon: Clock, color: 'bg-secondary-100 text-secondary-700' },
  ]);

  // Recent invoices list
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setDashboardLoading(true);

        const [jobsRes, appsRes, invoicesRes] = await Promise.all([
          api.get('/jobs/employer/my-jobs', { params: { limit: 1 } }),
          api.get('/applications', { params: { limit: 1 } }),
          invoiceAPI.getInvoices({ limit: 5 }),
        ]);

        // ----- Stats -----
        const jobStats = jobsRes.data?.data?.overallStats || {};
        const appStats = appsRes.data?.data?.stats || {};

        const updatedStats = [
          {
            title: 'Active Jobs',
            value: jobStats.active || 0,
            icon: Briefcase,
            color: 'bg-primary-100 text-primary-700',
          },
          {
            title: 'Total Applications',
            value: appStats.total || 0,
            icon: ClipboardList,
            color: 'bg-accent-100 text-accent-700',
          },
          {
            title: 'New Candidates',
            value: appStats.applied || 0,
            icon: Users,
            color: 'bg-warning-100 text-warning-700',
          },
          {
            title: 'Time to Hire',
            value: jobStats.timeToHire || '—',
            icon: Clock,
            color: 'bg-secondary-100 text-secondary-700',
          },
        ];
        setStats(updatedStats);

        // ----- Invoices -----
        const invoices = invoicesRes.data?.data || [];
        const sortedInvoices = invoices.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate)).slice(0, 4);
        setRecentInvoices(sortedInvoices);

      } catch (err) {
        console.error('Employer dashboard fetch error', err);
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Dummy data for charts
  const applicationData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Applications',
        data: [65, 59, 80, 81, 56, 55],
        backgroundColor: 'rgba(12, 158, 232, 0.2)',
        borderColor: 'rgba(12, 158, 232, 1)',
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: '#fff',
        pointBorderColor: 'rgba(12, 158, 232, 1)',
        pointRadius: 4,
      }
    ]
  };
  
  const hiringData = {
    labels: ['Front-end', 'Back-end', 'Design', 'Marketing', 'Other'],
    datasets: [
      {
        label: 'Hiring Distribution',
        data: [35, 25, 15, 15, 10],
        backgroundColor: [
          'rgba(12, 158, 232, 0.7)',
          'rgba(52, 180, 247, 0.7)',
          'rgba(34, 197, 94, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(99, 102, 241, 0.7)',
        ],
        borderWidth: 0,
      }
    ]
  };
  
  const candidateSourceData = {
    labels: ['LinkedIn', 'Job Boards', 'Referrals', 'Company Website', 'Other'],
    datasets: [
      {
        label: 'Candidates by Source',
        data: [42, 23, 15, 12, 8],
        backgroundColor: [
          'rgba(12, 158, 232, 0.8)',
          'rgba(52, 180, 247, 0.8)',
          'rgba(124, 206, 252, 0.8)',
          'rgba(186, 227, 253, 0.8)',
          'rgba(224, 241, 254, 0.8)',
        ],
        borderWidth: 0,
      }
    ]
  };
  
  // Dummy data for recent applications
  const recentApplications = [
    {
      id: 1,
      candidate: "Michael Johnson",
      position: "Senior Frontend Developer",
      date: "2 hours ago",
      status: "New",
      avatar: "M"
    },
    {
      id: 2,
      candidate: "Sarah Williams",
      position: "UX Designer",
      date: "5 hours ago",
      status: "Reviewed",
      avatar: "S"
    },
    {
      id: 3,
      candidate: "David Garcia",
      position: "Full Stack Developer",
      date: "1 day ago",
      status: "Interview",
      avatar: "D"
    },
    {
      id: 4,
      candidate: "Amanda Lee",
      position: "Marketing Manager",
      date: "2 days ago",
      status: "Offer",
      avatar: "A"
    }
  ];
  
  // Dummy data for top candidates
  const topCandidates = [
    {
      id: 1,
      name: "James Wilson",
      position: "Senior Backend Engineer",
      matchScore: 95,
      skills: ["Node.js", "PostgreSQL", "AWS"],
      avatar: "J"
    },
    {
      id: 2,
      name: "Emily Taylor",
      position: "Product Designer",
      matchScore: 92,
      skills: ["UI/UX", "Figma", "Design Systems"],
      avatar: "E"
    },
    {
      id: 3,
      name: "Robert Brown",
      position: "DevOps Engineer",
      matchScore: 89,
      skills: ["Docker", "Kubernetes", "CI/CD"],
      avatar: "R"
    }
  ];
  
  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page header */}
      <motion.div 
        className="flex flex-col md:flex-row md:items-center md:justify-between mb-8"
        variants={itemVariants}
      >
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back to your recruitment dashboard</p>
        </div>
        <div className="flex space-x-2 mt-4 md:mt-0">
          <Link 
            to="/jobs/new" 
            className="btn-primary flex items-center"
          >
            <Briefcase size={16} className="mr-2" />
            Post New Job
          </Link>
          <Link 
            to="/candidates" 
            className="btn-secondary flex items-center"
          >
            <Users size={16} className="mr-2" />
            View Candidates
          </Link>
        </div>
      </motion.div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            className="card card-hover"
            variants={itemVariants}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
          >
            <div className="flex items-start">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon size={20} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold mt-1 font-heading">{stat.value}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <TrendingUp size={16} className="text-success-500" />
              <span className="text-xs font-medium text-success-600 ml-1">12% increase</span>
              <span className="text-xs text-gray-500 ml-1">vs last month</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Applications over time */}
        <motion.div className="card card-hover" variants={itemVariants}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="subsection-title">Application Trends</h2>
            <div className="flex space-x-2">
              <button 
                className={`px-3 py-1 text-xs rounded-md ${timeframe === 'week' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                onClick={() => setTimeframe('week')}
              >
                Week
              </button>
              <button 
                className={`px-3 py-1 text-xs rounded-md ${timeframe === 'month' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                onClick={() => setTimeframe('month')}
              >
                Month
              </button>
              <button 
                className={`px-3 py-1 text-xs rounded-md ${timeframe === 'year' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                onClick={() => setTimeframe('year')}
              >
                Year
              </button>
            </div>
          </div>
          <div className="h-72">
            <Line 
              data={applicationData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                }
              }} 
            />
          </div>
        </motion.div>

        {/* Donut charts */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={itemVariants}>
          <div className="card card-hover">
            <h2 className="subsection-title mb-4">Hiring Distribution</h2>
            <div className="h-44 flex items-center justify-center">
              <Doughnut 
                data={hiringData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        padding: 15,
                        font: {
                          size: 11
                        }
                      }
                    }
                  },
                  cutout: '70%'
                }}
              />
            </div>
          </div>

          <div className="card card-hover">
            <h2 className="subsection-title mb-4">Candidate Sources</h2>
            <div className="h-44 flex items-center justify-center">
              <Doughnut 
                data={candidateSourceData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        padding: 15,
                        font: {
                          size: 11
                        }
                      }
                    }
                  },
                  cutout: '70%'
                }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom row: Recent applications, Top candidates, Recent invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent applications */}
        <motion.div className="card card-hover" variants={itemVariants}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="subsection-title">Recent Applications</h2>
            <Link to="/applications" className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium">
              View all <ChevronRight size={16} />
            </Link>
          </div>
          
          <div className="space-y-4">
            {recentApplications.map(app => {
              let statusClass = 'bg-gray-100 text-gray-700';
              if (app.status === 'New') statusClass = 'bg-primary-100 text-primary-700';
              if (app.status === 'Reviewed') statusClass = 'bg-accent-100 text-accent-700';
              if (app.status === 'Interview') statusClass = 'bg-warning-100 text-warning-700';
              if (app.status === 'Offer') statusClass = 'bg-success-100 text-success-700';
              
              return (
                <div key={app.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-700 font-medium">{app.avatar}</span>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{app.candidate}</h3>
                    <p className="text-xs text-gray-500">{app.position}</p>
                    <div className="flex items-center mt-1">
                      <span className="text-xs text-gray-500">{app.date}</span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${statusClass}`}>{app.status}</span>
                    </div>
                  </div>
                  <button className="p-1.5 hover:bg-gray-200 rounded-full">
                    <ChevronRight size={16} className="text-gray-400" />
                  </button>
                </div>
              )
            })}
          </div>
        </motion.div>

        {/* Top candidates */}
        <motion.div className="card card-hover" variants={itemVariants}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="subsection-title">Top Candidates</h2>
            <Link to="/candidates" className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium">
              View all <ChevronRight size={16} />
            </Link>
          </div>
          
          <div className="space-y-4">
            {topCandidates.map(candidate => (
              <div key={candidate.id} className="p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-700 font-medium">{candidate.avatar}</span>
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex justify-between">
                      <h3 className="text-sm font-medium text-gray-900">{candidate.name}</h3>
                      <div className="flex items-center">
                        <Star size={14} className="text-warning-500 mr-1" />
                        <span className="text-sm font-medium">{candidate.matchScore}%</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{candidate.position}</p>
                  </div>
                </div>
                
                <div className="mt-2 flex flex-wrap gap-1">
                  {candidate.skills.map((skill, i) => (
                    <span key={i} className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-100">
            <Link to="/candidates" className="flex justify-center items-center py-2 text-primary-600 hover:text-primary-700 text-sm font-medium">
              Browse Candidate Database <ArrowRight size={16} className="ml-1" />
            </Link>
          </div>
        </motion.div>

        {/* Recent Invoices */}
        <motion.div className="card card-hover" variants={itemVariants}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="subsection-title">Recent Invoices</h2>
            <Link to="/invoices" className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium">
              View all <ChevronRight size={16} />
            </Link>
          </div>

          {dashboardLoading ? (
            <div className="h-24 flex items-center justify-center">
              <div className="loading-spinner w-6 h-6 border-4" />
            </div>
          ) : recentInvoices.length === 0 ? (
            <p className="text-gray-600 text-sm">No invoices found.</p>
          ) : (
            <div className="space-y-4">
              {recentInvoices.map((inv) => (
                <Link key={inv._id} to={`/invoices/${inv._id}`} className="p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <ClipboardList size={18} className="text-primary-600" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</h3>
                    <p className="text-xs text-gray-500">{inv.clientName}</p>
                    <div className="flex items-center mt-1">
                      <span className="text-xs font-medium text-primary-600">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(inv.totalAmount || 0)}</span>
                      <span className="mx-1 text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{inv.status}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Dashboard; 